import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, Platform, ActivityIndicator, Image } from 'react-native';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useDailyDecision } from '../context/DailyDecisionContext';
import { usePersistentWidget } from '../context/PersistentWidgetContext';
import { useCharacterPopup } from '../context/CharacterPopupContext';
import DashboardSidebar from '../components/DashboardSidebar';
import PersistentWidgetIndicator from '../components/PersistentWidgetIndicator';
import AnimatedCard from '../components/AnimatedCard';
import AnimatedButton from '../components/AnimatedButton';
import RightSidebar from '../components/RightSidebar';
import WidgetRenderer from '../components/WidgetRenderer';
import MarkdownRenderer from '../components/MarkdownRenderer';
import SimpleRSKModal from '../components/SimpleRSKModal';
import IntakeFormPopup from '../components/IntakeFormPopup';
import { courseService } from '../services/courseService';
import { progressService } from '../services/progressService';
import { glossaryService } from '../services/glossaryService';
import { adminService } from '../services/adminService';

const CoursesScreen = () => {
  const { palette } = useTheme();
  const { user, updateUserCommitment } = useAuth();
  const { setCourseEngagement: setDailyDecisionEngagement, hasCompletedToday, todaysQuestion, hasBeenDismissedToday, openDailyDecision } = useDailyDecision();
  const { availableWidgets, openPersistentWidget, setCourseEngagement: setPersistentWidgetEngagement, getWidgetStatus } = usePersistentWidget();
  const { showCelebration, showEncouragement } = useCharacterPopup();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userProgress, setUserProgress] = useState({});
  
  // Navigation state
  const [expandedCourses, setExpandedCourses] = useState({});
  const [expandedChapters, setExpandedChapters] = useState({});
  const [selectedModule, setSelectedModule] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedChapter, setSelectedChapter] = useState(null);
  
  // Widget state - stores all available widgets from database
  const [allWidgets, setAllWidgets] = useState([]);
  
  // Scheduled Tasks state
  const [scheduledTasks, setScheduledTasks] = useState([]);
  const [taskAvailabilities, setTaskAvailabilities] = useState({});
  
  // Intake form state
  const [showIntakeForm, setShowIntakeForm] = useState(false);
  const [intakeLoading, setIntakeLoading] = useState(false);
  
  // Reward modal state
  const [showRewardModal, setShowRewardModal] = useState(false);

  // Fetch courses, progress, and widgets on mount
  useEffect(() => {
    loadCoursesAndProgress();
    loadWidgets(); // Load all widgets to resolve module widget references
  }, [user]);
  
  // Track course engagement separately to avoid infinite loops
  useEffect(() => {
    // Track engagement when screen mounts or module changes
    if (selectedModule) {
      setDailyDecisionEngagement(true);
      setPersistentWidgetEngagement(true);
    }
    
    // Clear engagement when unmounting or no module selected
    return () => {
      setDailyDecisionEngagement(false);
      setPersistentWidgetEngagement(false);
    };
  }, [selectedModule]); // Only depend on selectedModule, not the function



  // Check if we should show intake form when user or progress changes
  useEffect(() => {
    if (user && courses.length > 0 && !loading) {
      const hasAnyProgress = Object.keys(userProgress).length > 0;
      const shouldShowIntake = !user.hasCompletedIntake && !hasAnyProgress;

      console.log('Intake check:', {
        hasCompletedIntake: user.hasCompletedIntake,
        hasAnyProgress,
        shouldShowIntake
      });

      if (shouldShowIntake) {
        setShowIntakeForm(true);
      }
    }
  }, [user, userProgress, courses, loading]);

  const loadWidgets = async () => {
    try {
      const { widgets: fetchedWidgets, error } = await adminService.getAllWidgets();
      if (!error && fetchedWidgets) {
        setAllWidgets(fetchedWidgets);
        console.log('Loaded widgets:', fetchedWidgets.length);
      }
    } catch (error) {
      console.error('Error loading widgets:', error);
    }
  };

  // Resolve module widget references to actual widget objects
  const moduleWidgets = React.useMemo(() => {
    if (!selectedModule?.widgets || !allWidgets.length) return [];
    
    // Filter for sidebar widgets and resolve their IDs
    return selectedModule.widgets
      .filter(w => w.placement === 'sidebar' && w.widgetId)
      .map(widgetRef => {
        const fullWidget = allWidgets.find(w => w.id === widgetRef.widgetId);
        return fullWidget || null;
      })
      .filter(Boolean);
  }, [selectedModule, allWidgets]);
  
  // Debug: log widgets when module changes
  useEffect(() => {
    if (selectedModule) {
      console.log('=== MODULE WIDGETS DEBUG ===');
      console.log('Module:', selectedModule.title);
      console.log('Widget refs:', selectedModule.widgets);
      console.log('Resolved widgets:', moduleWidgets);
      console.log('All widgets available:', allWidgets.length);
      console.log('============================');
    }
  }, [selectedModule, moduleWidgets, allWidgets]);

  const loadCoursesAndProgress = async () => {
    setLoading(true);
    const { courses: fetchedCourses, error: fetchError } = await courseService.getAllCourses();
    
    if (fetchError) {
      setError(fetchError);
      setCourses([]);
    } else {
      console.log('=== COURSES LOADED ===');
      console.log('Number of courses:', fetchedCourses.length);
      fetchedCourses.forEach((course, idx) => {
        console.log(`Course ${idx + 1}:`, course.title);
        if (course.chapters) {
          course.chapters.forEach((chapter, chIdx) => {
            console.log(`  Chapter ${chIdx + 1}:`, chapter.title);
            if (chapter.modules) {
              chapter.modules.forEach((module, modIdx) => {
                console.log(`    Module ${modIdx + 1}:`, module.title);
                console.log(`      - Has content: ${!!(module.content || module.fullText)}`);
                console.log(`      - Content length: ${(module.content || module.fullText || '').length} chars`);
                console.log(`      - Has widgets: ${!!(module.widgets && module.widgets.length > 0)}`);
                console.log(`      - Widget count: ${module.widgets?.length || 0}`);
              });
            }
          });
        }
      });
      console.log('=====================');
      
      setCourses(fetchedCourses);
      setError(null);

      // Load progress for each course
      if (user && fetchedCourses.length > 0) {
        const progressData = {};
        for (const course of fetchedCourses) {
          const { progress } = await progressService.getCourseProgress(user.uid, course.id);
          if (progress) {
            progressData[course.id] = progress;
          }
        }
        setUserProgress(progressData);

        // Auto-select first available course and module (skip Coming Soon chapters)
        const firstCourse = fetchedCourses.find(c => c.status === 'Available') || fetchedCourses[0];
        if (firstCourse && firstCourse.chapters && firstCourse.chapters.length > 0) {
          // Find first available chapter
          const firstAvailableChapter = firstCourse.chapters.find(ch => ch.status !== 'Coming Soon') || firstCourse.chapters[0];
          if (firstAvailableChapter && firstAvailableChapter.modules && firstAvailableChapter.modules.length > 0) {
            // If user has existing progress or has completed intake, directly select module
            // Otherwise, let handleModuleSelect handle the intake form logic
            const hasAnyProgress = Object.keys(progressData).length > 0;
            if (user?.hasCompletedIntake || hasAnyProgress) {
              setSelectedCourse(firstCourse);
              setSelectedChapter(firstAvailableChapter);
              setSelectedModule(firstAvailableChapter.modules[0]);
            } else {
              handleModuleSelect(firstCourse, firstAvailableChapter, firstAvailableChapter.modules[0]);
            }
          }
        }
      }
    }
    
    setLoading(false);
  };

  const handleModuleSelect = async (course, chapter, module) => {
    console.log('=== MODULE SELECTED ===');
    console.log('Module Title:', module.title);
    console.log('Module Video URL:', module.videoUrl);
    console.log('Module Data:', module);
    console.log('Has completed intake:', user?.hasCompletedIntake);
    console.log('User progress count:', Object.keys(userProgress).length);
    console.log('======================');
    
    // Check if user has completed intake form AND has no existing progress before showing intake
    const hasAnyProgress = Object.keys(userProgress).length > 0;
    if (user && !user.hasCompletedIntake && !hasAnyProgress) {
      console.log('Showing intake form');
      setShowIntakeForm(true);
      return;
    }
    
    setSelectedCourse(course);
    setSelectedChapter(chapter);
    setSelectedModule(module);
    
    // Track course engagement to prevent auto-popup interruptions
    setCourseEngagement(true);

    // Enroll user if not already enrolled
    if (user && !userProgress[course.id]) {
      const { success } = await progressService.enrollInCourse(user.uid, course.id, course);
      if (success) {
        // Reload progress
        const { progress } = await progressService.getCourseProgress(user.uid, course.id);
        setUserProgress(prev => ({ ...prev, [course.id]: progress }));
      }
    }
  };

  const toggleCourse = (courseId) => {
    setExpandedCourses(prev => ({ ...prev, [courseId]: !prev[courseId] }));
  };

  const toggleChapter = (chapterId) => {
    setExpandedChapters(prev => ({ ...prev, [chapterId]: !prev[chapterId] }));
  };

  const handleMarkComplete = async () => {
    if (!selectedModule || !selectedCourse || !user) return;

    console.log('[CoursesScreen] Starting module completion...');
    const { success } = await progressService.completeModule(user.uid, selectedCourse.id, selectedModule.id, selectedModule.title);
    
    if (success) {
      console.log('[CoursesScreen] Module completed successfully, showing reward modal');
      // Show reward modal
      setShowRewardModal(true);
      
      // Show character celebration popup
      showCelebration(
        "Module Complete! 🎉",
        `Great job finishing "${selectedModule.title}"! Keep up the momentum!`
      );
      
      // Reload progress
      const { progress } = await progressService.getCourseProgress(user.uid, selectedCourse.id);
      setUserProgress(prev => ({ ...prev, [selectedCourse.id]: progress }));
    } else {
      console.error('[CoursesScreen] Module completion failed');
    }
  };

  // Handle intake form submission
  const handleIntakeSubmit = async (commitmentLevel) => {
    setIntakeLoading(true);
    
    const { success, error } = await updateUserCommitment(commitmentLevel);
    
    if (success) {
      setShowIntakeForm(false);
      
      // The updateUserCommitment function in AuthContext already updates the user state,
      // but we need to ensure it's reflected here. Let's add a small delay to let the context update.
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Continue with the course selection that was interrupted
      // We'll automatically select the first available course and module
      if (courses.length > 0) {
        const firstCourse = courses.find(c => c.status === 'Available') || courses[0];
        if (firstCourse && firstCourse.chapters && firstCourse.chapters.length > 0) {
          const firstAvailableChapter = firstCourse.chapters.find(ch => ch.status !== 'Coming Soon') || firstCourse.chapters[0];
          if (firstAvailableChapter && firstAvailableChapter.modules && firstAvailableChapter.modules.length > 0) {
            setSelectedCourse(firstCourse);
            setSelectedChapter(firstAvailableChapter);
            setSelectedModule(firstAvailableChapter.modules[0]);

            // Enroll user in the course
            if (!userProgress[firstCourse.id]) {
              const { success: enrollSuccess } = await progressService.enrollInCourse(user.uid, firstCourse.id, firstCourse);
              if (enrollSuccess) {
                const { progress } = await progressService.getCourseProgress(user.uid, firstCourse.id);
                setUserProgress(prev => ({ ...prev, [firstCourse.id]: progress }));
              }
            }
          }
        }
      }
    } else {
      // Show error message (you can implement toast notifications here)
      console.error('Failed to update commitment level:', error);
      alert('Failed to save your commitment level. Please try again.');
    }
    
    setIntakeLoading(false);
  };

  const handleIntakeClose = () => {
    setShowIntakeForm(false);
  };

  // Helper function to convert YouTube/Vimeo URLs to embed format
  const getEmbedUrl = (url) => {
    if (!url) return null;

    // YouTube: Convert various formats to embed URL
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      let videoId;
      
      // Extract video ID from different YouTube URL formats
      if (url.includes('youtube.com/embed/')) {
        return url; // Already an embed URL
      } else if (url.includes('youtube.com/watch?v=')) {
        videoId = url.split('v=')[1]?.split('&')[0];
      } else if (url.includes('youtu.be/')) {
        videoId = url.split('youtu.be/')[1]?.split('?')[0];
      }
      
      if (videoId) {
        return `https://www.youtube.com/embed/${videoId}`;
      }
    }
    
    // Vimeo: Convert to embed URL
    if (url.includes('vimeo.com')) {
      if (url.includes('player.vimeo.com/video/')) {
        return url; // Already an embed URL
      }
      const videoId = url.split('vimeo.com/')[1]?.split('?')[0];
      if (videoId) {
        return `https://player.vimeo.com/video/${videoId}`;
      }
    }
    
    return null; // Not a recognized embed URL
  };

  // Helper function to determine if URL is an embed (YouTube, Vimeo) or direct video
  const isEmbedUrl = (url) => {
    if (!url) return false;
    return url.includes('youtube.com') || url.includes('youtu.be') || 
           url.includes('vimeo.com');
  };

  // Render video player based on URL type
  const renderVideoPlayer = (videoUrl, title) => {
    if (!videoUrl) {
      console.error('No video URL provided');
      return null;
    }

    console.log('Rendering video player for URL:', videoUrl);

    // Check if it's an embed URL (YouTube, Vimeo)
    if (isEmbedUrl(videoUrl)) {
      const embedUrl = getEmbedUrl(videoUrl);
      
      if (!embedUrl) {
        console.error('Could not convert to embed URL:', videoUrl);
        return (
          <View style={[styles.videoFrameWrap, { alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255,255,255,0.05)' }]}>
            <Text style={{ color: '#EA806B', textAlign: 'center', padding: 20 }}>
              <Text>Invalid video URL format. Please check the URL.</Text>
            </Text>
          </View>
        );
      }

      console.log('Using embed URL:', embedUrl);
      
      // Use iframe for embed URLs
      return (
        <View style={styles.videoFrameWrap}>
          <iframe
            title={title || 'video'}
            width="100%"
            height="100%"
            src={embedUrl}
            style={{ border: '0', borderRadius: 16 }}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        </View>
      );
    } else {
      // Use HTML5 video element for direct URLs (Firebase Storage)
      console.log('Using HTML5 video player for direct URL');
      return (
        <View style={styles.videoFrameWrap}>
          <video
            controls
            controlsList="nodownload"
            style={{ width: '100%', height: '100%', borderRadius: 16, objectFit: 'contain', backgroundColor: '#000' }}
            onTimeUpdate={(e) => {
              if (user && selectedCourse && selectedModule) {
                const video = e.target;
                if (video.duration && !isNaN(video.duration)) {
                  const progress = (video.currentTime / video.duration) * 100;
                  if (progress > 0 && progress % 10 < 1) { // Update every 10%
                    progressService.updateVideoProgress(user.uid, selectedCourse.id, selectedModule.id, Math.floor(progress));
                  }
                }
              }
            }}
            onError={(e) => {
              console.error('Video playback error:', e);
              console.error('Failed video URL:', videoUrl);
            }}
            onLoadStart={() => {
              console.log('Video loading started');
            }}
            onLoadedData={() => {
              console.log('Video loaded successfully');
            }}
          >
            <source src={videoUrl} type="video/mp4" />
            <source src={videoUrl} type="video/webm" />
            <source src={videoUrl} type="video/ogg" />
            <Text>Your browser does not support the video tag.</Text>
          </video>
        </View>
      );
    }
  };

  const getModuleProgress = (courseId, moduleId) => {
    const courseProgress = userProgress[courseId];
    if (!courseProgress || !courseProgress.modules) return null;
    return courseProgress.modules[moduleId];
  };

  // Render course navigation content for sidebar
  const renderCourseNavigation = () => {
    if (loading) {
      return (
        <View style={styles.courseNavLoading}>
          <ActivityIndicator size="small" color={palette.primary || '#FF0600'} />
        </View>
      );
    }

    return (
      <View style={styles.courseNavigation}>
        
        {courses.map((course) => (
          <View key={course.id} style={styles.courseItem}>
            <TouchableOpacity
              style={styles.courseHeader}
              onPress={() => toggleCourse(course.id)}
            >
              <View style={styles.courseHeaderContent}>
                <Text style={styles.courseTitle}>
                  {expandedCourses[course.id] ? '▼' : '▶'} {course.title}
                </Text>
                {userProgress[course.id] && (
                  <Text style={styles.progressText}>
                    {userProgress[course.id].overallProgress}%
                  </Text>
                )}
              </View>
            </TouchableOpacity>

            {expandedCourses[course.id] && course.chapters && course.chapters.map((chapter) => {
              const hasModules = chapter.modules && chapter.modules.length > 0;
              const isPartLabel = !hasModules; // Chapters without modules are just section labels
              
              return (
                <View key={chapter.id} style={styles.chapterItem}>
                  {isPartLabel ? (
                    // Section label - no dropdown, just a label
                    <View style={styles.partLabelContainer}>
                      <Text style={styles.partLabel}>{chapter.title}</Text>
                    </View>
                  ) : (
                    // Regular chapter with modules - has dropdown
                    <>
                      <TouchableOpacity
                        style={styles.chapterHeader}
                        onPress={() => toggleChapter(chapter.id)}
                      >
                        <View style={styles.chapterHeaderContent}>
                          <Text style={styles.chapterTitle}>
                            {expandedChapters[chapter.id] ? '▼' : '▶'} {chapter.title}
                          </Text>
                          {chapter.status === 'Coming Soon' && (
                            <View style={styles.comingSoonBadge}>
                              <Text style={styles.comingSoonBadgeText}>Soon</Text>
                            </View>
                          )}
                        </View>
                      </TouchableOpacity>

                      {expandedChapters[chapter.id] && chapter.modules.map((module) => {
                        const moduleProgress = getModuleProgress(course.id, module.id);
                        const isCompleted = moduleProgress?.completed;
                        const isSelected = selectedModule?.id === module.id;

                        return (
                          <TouchableOpacity
                            key={module.id}
                            style={[
                              styles.moduleItem,
                              isSelected && styles.activeModuleItem,
                            ]}
                            onPress={() => handleModuleSelect(course, chapter, module)}
                          >
                            <Text style={[styles.moduleTitle, isSelected && styles.activeModuleTitle]}>
                              {isCompleted ? '✓' : '○'} {module.title}
                            </Text>
                            {module.required && (
                              <Text style={styles.requiredBadge}>*</Text>
                            )}
                          </TouchableOpacity>
                        );
                      })}
                    </>
                  )}
                </View>
              );
            })}
          </View>
        ))}
      </View>
    );
  };

  // Function to render daily decision reminder
  const renderDailyDecisionReminder = () => {
    // Only show if there's a question available, not completed, and has been dismissed
    if (!todaysQuestion || hasCompletedToday || !hasBeenDismissedToday) {
      return null;
    }
    
    return (
      <TouchableOpacity
        style={[styles.dailyDecisionReminder, {
          backgroundColor: 'rgba(255, 6, 0, 0.1)',
          borderColor: palette.primary || '#FF0600',
        }]}
        onPress={openDailyDecision}
      >
        <View style={styles.reminderContent}>
          <Text style={styles.reminderEmoji}>🎯</Text>
          <View style={styles.reminderText}>
            <Text style={[styles.reminderTitle, { color: palette.primary || '#FF0600' }]}>
              Daily Decision Available
            </Text>
            <Text style={[styles.reminderSubtitle, { color: palette.mutedText || 'rgba(255,255,255,0.7)' }]}>
              Tap to answer and earn RSK rewards
            </Text>
          </View>
          <Text style={[styles.reminderArrow, { color: palette.primary || '#FF0600' }]}>→</Text>
        </View>
      </TouchableOpacity>
    );
  };

  // Function to render persistent widget reminders/buttons - using same design as dashboard
  const renderPersistentWidgetReminders = () => {
    if (!availableWidgets || availableWidgets.length === 0) {
      return null;
    }

    return (
      <PersistentWidgetIndicator style={styles.persistentWidgetCard} />
    );
  };

  return (
    <View style={styles.container}>
      <View style={styles.layoutRow}>

        <View style={{gap: 24}}>
        {/* Course Navigation Sidebar */}
        <View style={[styles.courseSidebar,]}>
          <View>
            <Text style={styles.sidebarTitle}>Twelve Month Millionaire</Text>
          </View>
        </View>

        {/* Course Navigation Sidebar */}
        <View style={styles.courseSidebar}>
          {renderCourseNavigation()}
        </View>

        </View>

        {/* Main Content Area */}
        <ScrollView style={styles.mainCol} contentContainerStyle={styles.containerContent}>
          {/* Daily Decision Reminder - shows at top if dismissed */}
          {renderDailyDecisionReminder()}
          
          {/* Persistent Widget Reminders - shows all available widgets */}
          {renderPersistentWidgetReminders()}
          {loading ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="large" color={palette.primary} />
              <Text style={[styles.loadingText, { color: palette.mutedText || 'rgba(255,255,255,0.60)' }]}>
                <Text>Loading courses...</Text>
              </Text>
            </View>
          ) : error ? (
            <View style={styles.errorContainer}>
              <Text style={[styles.errorText, { color: '#EA806B' }]}>
                <Text>Failed to load courses: </Text>
                <Text>{error}</Text>
              </Text>
            </View>
          ) : !selectedModule ? (
            <View style={styles.emptyContainer}>
              <Text style={[styles.emptyText, { color: palette.mutedText || 'rgba(255,255,255,0.60)' }]}>
                <Text>Select a module from the sidebar to begin</Text>
              </Text>
            </View>
          ) : selectedChapter?.status === 'Coming Soon' ? (
            // Show Coming Soon page for chapters marked as coming soon
            <View style={styles.comingSoonContainer}>
              <Image
                source={require('../assets/character_stills/time_static.png')}
                style={styles.comingSoonImage}
                resizeMode="contain"
              />
              <Text style={[styles.comingSoonTitle, { color: palette.text || 'white' }]}>
                <Text>Chapter Coming Soon</Text>
              </Text>
              <Text style={[styles.comingSoonSubtitle, { color: palette.mutedText || 'rgba(255,255,255,0.7)' }]}>
                <Text>{selectedChapter.title}</Text>
              </Text>
              <Text style={[styles.comingSoonDescription, { color: palette.mutedText || 'rgba(255,255,255,0.6)' }]}>
                <Text>This chapter is currently being developed. Check back soon!</Text>
              </Text>
              {selectedChapter.description && (
                <AnimatedCard style={[styles.contentCard, { backgroundColor: palette.surface, borderColor: palette.border || 'rgba(255,255,255,0.16)', marginTop: 32 }]}>
                  <Text style={[styles.contentTitle, { color: palette.text || 'white' }]}>
                    <Text>What to Expect</Text>
                  </Text>
                  <MarkdownRenderer content={selectedChapter.description} />
                </AnimatedCard>
              )}
            </View>
          ) : (
            <View style={styles.moduleContent}>
              {/* Module Header */}
              <View style={styles.moduleHeader}>
                <View style={{ flex: 1, minWidth: 0 }}>
                  <Text style={styles.moduleBreadcrumbCourse}>
                    {selectedCourse?.title}
                  </Text>
                  <Text style={styles.moduleBreadcrumbChapter}>
                    {selectedChapter?.title}
                  </Text>
                  <Text style={styles.moduleMainTitle}>
                    {selectedModule.title}
                  </Text>
                </View>
                {selectedModule.required && (
                  <View style={styles.requiredPill}>
                    <Text style={styles.requiredPillText}>Required</Text>
                  </View>
                )}
              </View>



              {/* Video Player */}
              {Platform.OS === 'web' && (
                <View style={styles.videoSection}>
                  {selectedModule.videoUrl ? (
                    <>
                      {renderVideoPlayer(selectedModule.videoUrl, selectedModule.title)}
                      {/* Debug info - remove this after testing */}
                      <View style={[styles.debugInfo, { backgroundColor: 'rgba(255,255,255,0.05)', borderColor: palette.border || 'rgba(255,255,255,0.16)' }]}>
                        <Text style={[styles.debugText, { color: palette.mutedText }]}>
                          <Text>Video URL: </Text>
                          <Text style={{ fontSize: 10, fontFamily: 'monospace' }}>{selectedModule.videoUrl}</Text>
                        </Text>
                      </View>
                    </>
                  ) : (
                    <View style={[styles.noVideoPlaceholder, { backgroundColor: 'rgba(255,255,255,0.03)', borderColor: palette.border || 'rgba(255,255,255,0.16)' }]}>
                      <Text style={[styles.noVideoText, { color: palette.mutedText }]}>
                        <Text>No video available for this module</Text>
                      </Text>
                    </View>
                  )}
                </View>
              )}

              {/* Module Content */}
              <AnimatedCard style={[styles.contentCard,]}>
                {selectedModule.content || selectedModule.fullText ? (
                  <MarkdownRenderer 
                    content={selectedModule.content || selectedModule.fullText}
                    style={styles.markdownContent}
                    customColors={{
                      text: '#F1F1F1',
                      mutedText: '#F1F1F1',
                      primary: '#F1F1F1'
                    }}
                  />
                ) : (
                  <>
                    <Text style={[styles.contentTitle,]}>
                      <Text>Module Content</Text>
                    </Text>
                    <Text style={[styles.contentText, ]}>
                      <Text>{selectedModule.description || 'No content available for this module.'}</Text>
                    </Text>
                  </>
                )}
              </AnimatedCard>



              {/* Complete Button */}
              {user && (
                <View style={styles.actionSection}>
                  {getModuleProgress(selectedCourse.id, selectedModule.id)?.completed ? (
                    <View style={styles.completedBanner}>
                      <Text style={styles.completedText}>✓ Module Completed</Text>
                    </View>
                  ) : (
                    <TouchableOpacity
                      style={styles.completeButton}
                      onPress={handleMarkComplete}
                    >
                      <Text style={styles.completeButtonText}>Mark as Complete →</Text>
                    </TouchableOpacity>
                  )}
                </View>
              )}
            </View>
          )}
        </ScrollView>

        {/* Right Sidebar with Widgets */}
        {moduleWidgets.length > 0 && (
          <RightSidebar
            showGlossary={false}
            widgets={moduleWidgets}
          />
        )}
      </View>

      {/* RSK Reward Modal */}
      <SimpleRSKModal
        visible={showRewardModal}
        onClose={() => setShowRewardModal(false)}
        rskAmount={10}
      />

      {/* Intake Form Popup */}
      <IntakeFormPopup
        visible={showIntakeForm}
        onClose={handleIntakeClose}
        onSubmit={handleIntakeSubmit}
        loading={intakeLoading}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingLeft: 36,
    paddingRight: 10,
    paddingTop: 160,
    paddingBottom: 40,
    minHeight: '100vh',
    ...Platform.select({
      web: {
        overflow: 'visible',
      },
    }),
  },
  layoutRow: {
    flex: 1,
    flexDirection: 'row',
    ...Platform.select({
      web: {
        overflow: 'visible',
      },
    }),
  },
  courseSidebar: {
    paddingHorizontal: 24,
    paddingVertical: 24,
    width: 300,
    backgroundColor: '#FF4747',
    borderRadius: 20,
    marginRight: 24,
    ...Platform.select({
      web: {
        boxShadow: '5px 7px 0px rgba(0,0,0,1)',
        maxHeight: 'calc(100vh - 140px)',
        overflowY: 'auto',
        overflowX: 'hidden',
      },
    }),
  },
  sidebarTitle: {
    color: 'white',
    fontSize: 18,
    fontFamily: 'Chivo',
    fontWeight: '700',
    ...Platform.select({
      web: {
        textShadow: '2px 2px 0px rgba(0,0,0,0.8)',
      },
      default: {
        textShadowColor: 'rgba(0,0,0,0.8)',
        textShadowOffset: { width: 2, height: 2 },
        textShadowRadius: 0,
      },
    }),
  },
  courseNavigation: {
    padding: 0,
    gap: 4,
    backgroundColor: '#FF4747',
  },
  courseNavLoading: {
    paddingVertical: 20,
    alignItems: 'center',
  },
  divider: {
    height: 1,
    marginVertical: 12,
    marginHorizontal: -16,
  },
  courseItem: {
    marginBottom: 8,
  },
  courseHeader: {
    paddingVertical: 8,
    paddingHorizontal: 4,
    borderRadius: 8,
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  courseHeaderContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 8,
  },
  courseTitle: {
    fontSize: 14,
    fontFamily: 'Chivo',
    fontWeight: '600',
    flex: 1,
    lineHeight: 20,
    color: 'white',
    ...Platform.select({
      web: {
        textShadow: '1px 1px 0px rgba(0,0,0,0.8)',
      },
      default: {
        textShadowColor: 'rgba(0,0,0,0.8)',
        textShadowOffset: { width: 1, height: 1 },
        textShadowRadius: 0,
      },
    }),
  },
  progressText: {
    fontSize: 11,
    fontFamily: 'Archivo',
    fontWeight: '700',
    color: 'white',
    backgroundColor: 'rgba(0,0,0,0.2)',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
    ...Platform.select({
      web: {
        textShadow: '1px 1px 0px rgba(0,0,0,0.6)',
      },
      default: {
        textShadowColor: 'rgba(0,0,0,0.6)',
        textShadowOffset: { width: 1, height: 1 },
        textShadowRadius: 0,
      },
    }),
  },
  chapterItem: {
    marginLeft: 8,
    marginTop: 8,
    marginBottom: 4,
  },
  partLabelContainer: {
    paddingVertical: 10,
    paddingHorizontal: 8,
    borderTopWidth: 1,
    borderTopColor: 'rgba(255,255,255,0.15)',
    marginTop: 8,
  },
  partLabel: {
    fontSize: 11,
    fontFamily: 'Archivo',
    fontWeight: '700',
    color: 'rgba(255,255,255,0.8)',
    textTransform: 'uppercase',
    letterSpacing: 1,
    ...Platform.select({
      web: {
        textShadow: '1px 1px 0px rgba(0,0,0,0.6)',
      },
      default: {
        textShadowColor: 'rgba(0,0,0,0.6)',
        textShadowOffset: { width: 1, height: 1 },
        textShadowRadius: 0,
      },
    }),
  },
  chapterHeader: {
    paddingVertical: 10,
    paddingHorizontal: 8,
    borderRadius: 8,
    backgroundColor: 'rgba(0,0,0,0.1)',
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  chapterHeaderContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  chapterTitle: {
    fontSize: 13,
    fontFamily: 'Public Sans',
    fontWeight: '600',
    flex: 1,
    lineHeight: 18,
    color: 'white',
    ...Platform.select({
      web: {
        textShadow: '1px 1px 0px rgba(0,0,0,0.8)',
      },
      default: {
        textShadowColor: 'rgba(0,0,0,0.8)',
        textShadowOffset: { width: 1, height: 1 },
        textShadowRadius: 0,
      },
    }),
  },
  comingSoonBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 8,
    backgroundColor: 'rgba(255,255,255,0.2)',
  },
  comingSoonBadgeText: {
    fontSize: 9,
    fontFamily: 'Archivo',
    fontWeight: '700',
    color: 'white',
    ...Platform.select({
      web: {
        textShadow: '1px 1px 0px rgba(0,0,0,0.5)',
      },
    }),
  },
  moduleItem: {
    marginLeft: 8,
    marginTop: 4,
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderRadius: 6,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...Platform.select({
      web: {
        cursor: 'pointer',
        transition: 'all 0.2s ease',
      },
    }),
  },
  activeModuleItem: {
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    ...Platform.select({
      web: {
        boxShadow: '2px 3px 0px rgba(0,0,0,0.8)',
      },
    }),
  },
  activeModuleTitle: {
    color: '#000000',
    textShadowColor: 'transparent',
    ...Platform.select({
      web: {
        textShadow: 'none',
      },
    }),
  },
  moduleTitle: {
    fontSize: 13,
    fontFamily: 'Archivo',
    flex: 1,
    lineHeight: 18,
    color: 'white',
    ...Platform.select({
      web: {
        textShadow: '1px 1px 0px rgba(0,0,0,0.8)',
      },
      default: {
        textShadowColor: 'rgba(0,0,0,0.8)',
        textShadowOffset: { width: 1, height: 1 },
        textShadowRadius: 0,
      },
    }),
  },
  requiredBadge: {
    fontSize: 12,
    fontWeight: '700',
    marginLeft: 4,
    color: 'rgba(255,255,255,0.7)',
  },
  mainCol: {
    flex: 1,
    padding: 24,
    ...Platform.select({
      web: {
        overflow: 'visible',
      },
    }),
  },
  glossaryCol: {
    width: 320,
    ...Platform.select({
      web: {
        position: 'sticky',
        top: 100,
      },
    }),
  },
  containerContent: {
    paddingBottom: 60,
    paddingRight: 12,
    ...Platform.select({
      web: {
        overflow: 'visible',
      },
    }),
  },
  loadingContainer: {
    padding: 60,
    alignItems: 'center',
    gap: 16,
  },
  loadingText: {
    fontSize: 15,
    fontFamily: 'Archivo',
  },
  errorContainer: {
    padding: 40,
    alignItems: 'center',
    backgroundColor: 'rgba(234, 128, 107, 0.1)',
    borderRadius: 16,
    marginVertical: 20,
  },
  errorText: {
    fontSize: 15,
    fontFamily: 'Archivo',
    textAlign: 'center',
  },
  emptyContainer: {
    padding: 60,
    alignItems: 'center',
  },
  emptyText: {
    fontSize: 17,
    fontFamily: 'Archivo',
    textAlign: 'center',
  },
  moduleContent: {
    gap: 24,
  },
  moduleHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    backgroundColor: '#FF4747',
    borderRadius: 20,
    padding: 24,
     ...Platform.select({
          web: {
              boxShadow: '5px 7px 0px rgba(0,0,0,1)',
          },
      }),
  },
  moduleBreadcrumb: {
    fontSize: 13,
    fontFamily: 'Archivo',
    marginBottom: 4,
    flexShrink: 1,
    color: '#F1F1F1',
    ...Platform.select({
      web: {
        textShadow: '1px 1px 0px rgba(0,0,0,1)',
      },
    }),
  },
  moduleBreadcrumbCourse: {
    fontSize: 12,
    fontFamily: 'Archivo',
    fontWeight: '600',
    color: 'rgba(255,255,255,0.8)',
    marginBottom: 2,
    ...Platform.select({
      web: {
        textShadow: '1px 1px 0px rgba(0,0,0,0.8)',
      },
    }),
  },
  moduleBreadcrumbChapter: {
    fontSize: 14,
    fontFamily: 'Archivo',
    fontWeight: '500',
    color: 'rgba(255,255,255,0.9)',
    marginBottom: 8,
    ...Platform.select({
      web: {
        textShadow: '1px 1px 0px rgba(0,0,0,0.8)',
      },
    }),
  },
  moduleMainTitle: {
    fontSize: 36,
    fontFamily: 'Archivo',
    fontWeight: '700',
    flexShrink: 1,
    flexWrap: 'wrap',
    color: '#F1F1F1',
    lineHeight: 42,
    ...Platform.select({
      web: {
        textShadow: '3px 3px 0px rgba(0,0,0,1)',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
      },
    }),
  },
  requiredPill: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#F1F1F1', 
  },
  requiredPillText: {
    fontSize: 12,
    fontFamily: 'Archivo',
    fontWeight: '400',
    color: '#F1F1F1', 
  },
  videoSection: {
    marginBottom: 24,
  },
  videoFrameWrap: {
    width: '100%',
    aspectRatio: 16 / 9,
    borderRadius: 16,
    overflow: 'hidden',
    ...Platform.select({
      web: {
        boxShadow: '0px 4px 12px rgba(0,0,0,0.25)',
      },
    }),
  },
  debugInfo: {
    marginTop: 8,
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
  },
  debugText: {
    fontSize: 11,
    fontFamily: 'Archivo',
    wordBreak: 'break-all',
  },
  noVideoPlaceholder: {
    width: '100%',
    aspectRatio: 16 / 9,
    borderRadius: 16,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
  },
  noVideoText: {
    fontSize: 14,
    fontFamily: 'Archivo',
    textAlign: 'center',
  },
  contentCard: {
    borderRadius: 20,
    padding: 24,
      backgroundColor: '#FF4747',
    borderRadius: 20,
    padding: 24,
     ...Platform.select({
          web: {
              boxShadow: '5px 7px 0px rgba(0,0,0,1)',
          },
      }),
  },
  contentTitle: {
    fontSize: 20,
    fontFamily: 'Archivo',
    fontWeight: '700',
    marginBottom: 12,
    color: '#F1F1F1',
        ...Platform.select({
      web: {
        textShadow: '1px 1px 0px rgba(0,0,0,1)',
      },
    }),
  },
  contentText: {
    fontSize: 15,
    fontFamily: 'Archivo',
    lineHeight: 24,
    color: '#F1F1F1',
        ...Platform.select({
      web: {
        textShadow: '1px 1px 0px rgba(0,0,0,1)',
      },
    }),
  },
  actionSection: {
    marginTop: 24,
    alignItems: 'center',
  },
  completeButton: {
    backgroundColor: 'white',
    paddingVertical: 16,
    paddingHorizontal: 32,
    borderRadius: 12,
    borderWidth: 3,
    borderColor: '#000000',
    ...Platform.select({
      web: {
        boxShadow: '4px 5px 0px rgba(0,0,0,1)',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
      },
      default: {
        shadowColor: '#000000',
        shadowOffset: { width: 4, height: 5 },
        shadowOpacity: 1,
        shadowRadius: 0,
        elevation: 5,
      },
    }),
  },
  completeButtonText: {
    fontSize: 16,
    fontFamily: 'Archivo',
    fontWeight: '700',
    color: '#000000',
  },
  completedBanner: {
    backgroundColor: '#22C55E',
    paddingVertical: 16,
    paddingHorizontal: 32,
    borderRadius: 12,
    borderWidth: 3,
    borderColor: '#000000',
    ...Platform.select({
      web: {
        boxShadow: '4px 5px 0px rgba(0,0,0,1)',
      },
      default: {
        shadowColor: '#000000',
        shadowOffset: { width: 4, height: 5 },
        shadowOpacity: 1,
        shadowRadius: 0,
        elevation: 5,
      },
    }),
  },
  completedText: {
    fontSize: 16,
    fontFamily: 'Archivo',
    fontWeight: '700',
    color: 'white',
    ...Platform.select({
      web: {
        textShadow: '1px 1px 0px rgba(0,0,0,0.5)',
      },
    }),
  },
  comingSoonContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 60,
    paddingHorizontal: 40,
  },
  comingSoonImage: {
    width: 200,
    height: 200,
    marginBottom: 24,
  },
  comingSoonTitle: {
    fontSize: 36,
    fontFamily: 'Archivo',
    fontWeight: '700',
    marginBottom: 12,
    textAlign: 'center',
  },
  comingSoonSubtitle: {
    fontSize: 24,
    fontFamily: 'Archivo',
    fontWeight: '600',
    marginBottom: 16,
    textAlign: 'center',
  },
  comingSoonDescription: {
    fontSize: 16,
    fontFamily: 'Archivo',
    lineHeight: 24,
    textAlign: 'center',
    maxWidth: 600,
  },
  dailyDecisionReminder: {
    marginBottom: 16,
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    ...Platform.select({
      web: {
        cursor: 'pointer',
        transition: 'all 0.2s ease',
      },
    }),
  },
  reminderContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  reminderEmoji: {
    fontSize: 24,
  },
  reminderText: {
    flex: 1,
  },
  reminderTitle: {
    fontSize: 14,
    fontFamily: 'Archivo',
    fontWeight: '600',
    marginBottom: 2,
  },
  reminderSubtitle: {
    fontSize: 12,
    fontFamily: 'Archivo',
    fontWeight: '500',
  },
  reminderArrow: {
    fontSize: 16,
    fontWeight: '600',
  },
  persistentWidgetCard: {
    marginBottom: 16,
  },
  markdownContent: {
    // Custom styles for markdown content
    // Note: Individual text colors are controlled by MarkdownRenderer's theme usage
    // You can add container-level styles here
  },
});

export default CoursesScreen;
